import React, { useMemo, useState } from 'react';
import KickImage from 'Reuseables/KickImage';
import Styles from './ModalBulkUpload.module.scss';
import { Modal, ModalProps, Row, Col, Button } from 'antd';
import 'antd/es/row/style/css';
import 'antd/es/col/style/css';
import 'antd/es/modal/style/css';
import 'antd/es/button/style/css';
import { CustomDropzone } from 'components/Forms';
import PlaceholderUploadIcon from 'images/csv-icon.png';
import PlaceholderUploadErrorIcon from 'images/csv-error-icon.png';
import { useFetchDownloadTemplate } from './Hooks/useFetchDownloadTemplate';
import { useUploadProducts } from './Hooks/useUploadProducts';
import Loader from './Components/Loader';

interface IModalBulkUploadProps extends ModalProps {
  action: 'add' | 'edit';
  open?: boolean;
  visible?: boolean;
  fetchSellerProducts: () => void;
}

interface ISelectedSourceState {
  file: File | null;
  fileName: string | null;
  fileUrl: string | null;
}

const ModalBulkUpload: React.FC<IModalBulkUploadProps> = ({
  action,
  fetchSellerProducts,
  onCancel,
  ...props
}) => {
  const [selectedSource, setSelectedSource] = useState<ISelectedSourceState>({
    file: null,
    fileName: null,
    fileUrl: null,
  });

  const {
    isFetching,
    isSuccess,
    isError,
    isUnexpectedError,
    setIsSuccess,
    setIsError,
    setIsUnexpectedError,
    upload,
    downloadFeedbackFile,
  } = useUploadProducts({});
  const { download: downloadTemplateFile, isFetching: isFetchingDownload } =
    useFetchDownloadTemplate({
      action,
    });

  const notFetchingNotError = useMemo(() => {
    return !isFetching && !isError && !isUnexpectedError;
  }, [isFetching, isError, isUnexpectedError]);

  const notFetchingButError = useMemo(() => {
    const isAnyError = isError || isUnexpectedError;
    return !isFetching && isAnyError;
  }, [isFetching, isError, isUnexpectedError]);

  const isUploadDisabled = useMemo(() => {
    return isFetching || isError || isSuccess || isUnexpectedError;
  }, [isFetching, isError, isSuccess, isUnexpectedError]);

  const isSubmitDisabled = useMemo(() => {
    if (isSuccess) {
      return false;
    }

    return isFetching || !selectedSource.file || isUploadDisabled;
  }, [isFetching, selectedSource.file, isUploadDisabled, isSuccess]);

  const title = useMemo(() => {
    const selectedAction = action === 'add' ? 'Add' : 'Edit';
    return `${selectedAction} Product(s)`;
  }, [action]);

  const clearSelectedFile = () => {
    setSelectedSource({
      file: null,
      fileName: null,
      fileUrl: null,
    });
  };

  const onSelectFile = async (file) => {
    if (isError) {
      setIsError(false);
    }

    const fileName = file.name || '';
    const fileUrl = URL.createObjectURL(file);
    setSelectedSource({
      file,
      fileName,
      fileUrl,
    });
  };

  const onSubmit = async (e) => {
    if (isSuccess) {
      onCloseModal(e);
      return;
    }

    if (!selectedSource.file) {
      return;
    }

    await upload(selectedSource.file);
    fetchSellerProducts();
  };

  const resetState = () => {
    clearSelectedFile();
    setIsError(false);
    setIsUnexpectedError(false);
    setIsSuccess(false);
  };

  const onCloseModal = (e) => {
    resetState();

    if (onCancel) {
      onCancel(e);
    }
  };

  return (
    <Modal
      width={450}
      style={{ borderRadius: 24 }}
      footer={null}
      onCancel={onCloseModal}
      {...props}
    >
      <Row>
        <div className={Styles['title']}>{title}</div>
      </Row>
      <div className={Styles['content-wrapper']}>
        <div>
          <div className={Styles['sub-title']}>1. Download Template</div>
          <div className={Styles['regular-text']}>
            Download and fill in the template. Only csv format is accepted.
          </div>
          <div
            className={
              isFetchingDownload
                ? Styles['download-template-disabled']
                : Styles['download-template']
            }
            onClick={downloadTemplateFile}
          >
            {isFetchingDownload ? 'Downloading...' : 'Download Template'}
          </div>
        </div>
        <div>
          <div className={Styles['sub-title']}>2. Upload</div>
          <div className={Styles['regular-text']}>
            After completing the template, upload your file.
          </div>
          <div style={{ marginTop: 16 }}>
            <CustomDropzone
              accept={'.csv'}
              setPhoto={(file) => {
                onSelectFile(file);
              }}
              noClick={isError || isSuccess || isFetching}
              className={`upload-photo-wrapper`}
            >
              <Row
                align="middle"
                justify="center"
                className={Styles['uploader']}
              >
                <Col
                  span={24}
                  style={{
                    height: 'fit-content',
                    alignSelf: 'center',
                    width: '100%',
                    padding: 24,
                  }}
                >
                  {isFetching && (
                    <div>
                      <Loader isVisible={isFetching} />
                      <Row
                        justify="center"
                        align="middle"
                        className={Styles['uploading']}
                      >
                        Uploading file, please wait...
                      </Row>
                    </div>
                  )}

                  {notFetchingButError && (
                    <div className={Styles['preview-wrapper']}>
                      <KickImage
                        src={PlaceholderUploadErrorIcon}
                        alt="upload image"
                        className="upload-package-photo"
                        style={{ width: 32 }}
                      />

                      <div className={Styles['file-name']}>Whoops!</div>

                      {isUnexpectedError && (
                        <div className={Styles['description']}>
                          There’s an unexpected issue in the server. Please try
                          again later or contact our customer service.
                        </div>
                      )}

                      {!isUnexpectedError && (
                        <>
                          <div className={Styles['description']}>
                            There’s an issue with the file you uploaded. Review
                            the file below and try uploading it again.
                          </div>
                          <Row className={Styles['button-error-wrapper']}>
                            <Col span={12}>
                              <Button
                                onClick={downloadFeedbackFile}
                                className={Styles['download-file']}
                                shape="round"
                              >
                                Download File
                              </Button>
                            </Col>
                            <Col span={12}>
                              <Button
                                onClick={() => {
                                  resetState();
                                }}
                                className={Styles['upload-file']}
                                shape="round"
                              >
                                Upload File
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}

                  {notFetchingNotError && selectedSource.file && (
                    <div className={Styles['preview-wrapper']}>
                      <KickImage
                        src={PlaceholderUploadIcon}
                        alt="upload image"
                        className="upload-package-photo"
                        style={{ width: 32 }}
                      />
                      <div className={Styles['file-name']}>
                        {selectedSource.fileName}
                      </div>

                      {isSuccess && (
                        <div className={Styles['description']}>
                          Your file has been successfully submitted!
                        </div>
                      )}

                      {!isSuccess && (
                        <>
                          <div className={Styles['description']}>
                            Make sure you have followed the instructions,
                            correct software and format, and have a stable
                            internet connection.
                          </div>

                          <div className={Styles['change-file']}>
                            Change File
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {notFetchingNotError && !selectedSource.fileUrl && (
                    <div style={{ textAlign: 'center' }}>
                      <KickImage
                        src={PlaceholderUploadIcon}
                        alt="upload image"
                        className="upload-package-photo"
                        style={{ width: 32 }}
                      />
                      <p className="upload-title" style={{ paddingTop: 8 }}>
                        Drag and Drop files, or <span>Browse</span>
                      </p>
                      <span className={Styles['note']}>csv files only</span>
                    </div>
                  )}
                </Col>
              </Row>
            </CustomDropzone>
          </div>
        </div>
        <Button
          className={
            isSubmitDisabled
              ? Styles['submit-button-disabled']
              : Styles['submit-button']
          }
          shape="round"
          disabled={isSubmitDisabled}
          onClick={onSubmit}
          block
        >
          {isSuccess ? 'Done' : 'Submit'}
        </Button>
      </div>
    </Modal>
  );
};

export default ModalBulkUpload;
